import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Play, Square, CheckCircle2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { micConstraints, getMicId, setMicId, createMeter, pickMime, SPEECH_LEVEL } from "@/lib/mic";

const SoundBar = ({ level }) => (
  <div data-testid="mic-test-sound-bar" className="flex items-end gap-1 h-16" aria-label="Microphone level">
    {Array.from({ length: 24 }, (_, i) => {
      const on = level * 24 > i;
      const color = i < 14 ? "var(--ok)" : i < 20 ? "var(--warn)" : "var(--alert)";
      return <span key={i} className="flex-1 rounded-sm transition-[height,background-color] duration-75" style={{ height: `${20 + (i / 24) * 80}%`, background: on ? color : "var(--muted-bg)" }} />;
    })}
  </div>
);

const useMicLevel = (open, deviceId) => {
  const [state, setState] = useState({ level: 0, heard: false, error: "", label: "", stream: null });
  useEffect(() => {
    if (!open) return undefined;
    let meter, stream, timer, cancelled = false;
    const t0 = Date.now();
    navigator.mediaDevices.getUserMedia(micConstraints(deviceId)).then((s) => {
      if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
      stream = s;
      meter = createMeter(s);
      let heard = false;
      const label = s.getAudioTracks()[0]?.label || "Microphone";
      timer = setInterval(() => {
        const rms = meter.read();
        if (rms > SPEECH_LEVEL) heard = true;
        setState({ level: Math.min(1, rms / (SPEECH_LEVEL * 4)), heard, quiet: !heard && Date.now() - t0 > 5000, error: "", label, stream: s });
      }, 60);
    }).catch((e) => setState((p) => ({ ...p, error: e?.name === "NotAllowedError" ? "Microphone access is blocked. Click the lock/mic icon in the address bar, allow the microphone, then reopen this test." : "No microphone could be opened. Check one is connected and not in use by another app." })));
    return () => { cancelled = true; clearInterval(timer); meter?.close(); stream?.getTracks().forEach((t) => t.stop()); };
  }, [open, deviceId]);
  return state;
};

const Playback = ({ stream }) => {
  const [phase, setPhase] = useState("idle");
  const url = useRef("");
  const record = useCallback(() => {
    if (!stream) return;
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      URL.revokeObjectURL(url.current);
      url.current = URL.createObjectURL(new Blob(chunks, { type: rec.mimeType }));
      setPhase("playing");
      const a = new Audio(url.current);
      a.onended = () => setPhase("idle");
      a.play().catch(() => setPhase("idle"));
    };
    rec.start();
    setPhase("recording");
    setTimeout(() => rec.state !== "inactive" && rec.stop(), 4000);
  }, [stream]);
  useEffect(() => () => URL.revokeObjectURL(url.current), []);
  return (
    <button data-testid="mic-test-playback-button" onClick={record} disabled={!stream || phase !== "idle"} className="btn-ghost h-10 px-4 rounded-full text-sm font-medium flex items-center gap-2 disabled:opacity-60">
      {phase === "recording" ? <><Square size={14} fill="currentColor" style={{ color: "var(--alert)" }} /> Recording 4 seconds…</> : phase === "playing" ? <><Play size={14} /> Playing back…</> : <><Mic size={14} /> Record 4 s and play back</>}
    </button>
  );
};

const DevicePicker = ({ value, onChange, open }) => {
  const [devices, setDevices] = useState([]);
  useEffect(() => {
    if (!open) return;
    const load = () => navigator.mediaDevices.enumerateDevices().then((d) => setDevices(d.filter((x) => x.kind === "audioinput" && x.label)));
    const t = setTimeout(load, 400);
    navigator.mediaDevices.addEventListener?.("devicechange", load);
    return () => { clearTimeout(t); navigator.mediaDevices.removeEventListener?.("devicechange", load); };
  }, [open]);
  if (devices.length < 2) return null;
  return (
    <label className="block text-sm">
      <span className="font-medium">Microphone</span>
      <select data-testid="mic-test-device-select" value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-10 rounded-lg px-3 text-sm" style={{ background: "var(--muted-bg)", border: "1px solid var(--line)", color: "var(--ink)" }}>
        <option value="">System default</option>
        {devices.filter((d) => d.deviceId !== "default" && d.deviceId !== "communications").map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)}
      </select>
    </label>
  );
};

export const MicTestDialog = ({ open, onOpenChange }) => {
  const [deviceId, setDeviceId] = useState(getMicId);
  const { level, heard, quiet, error, label, stream } = useMicLevel(open, deviceId);
  const choose = (id) => { setMicId(id); setDeviceId(id); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mic-test-dialog" className="sm:max-w-md" style={{ background: "var(--surface)" }}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Mic size={18} /> Test microphone</DialogTitle>
          <DialogDescription>Speak normally. The bars should jump when you talk.</DialogDescription>
        </DialogHeader>
        {error ? (
          <p data-testid="mic-test-error" className="text-sm flex gap-2" style={{ color: "var(--alert)" }}><MicOff size={16} className="shrink-0 mt-0.5" /> {error}</p>
        ) : (
          <div className="space-y-4">
            <p data-testid="mic-test-device-label" className="text-xs font-mono truncate" style={{ color: "var(--ink-3)" }}>Using: {label || "…"}</p>
            <SoundBar level={level} />
            <p data-testid="mic-test-status" className="text-sm font-medium flex items-center gap-2" style={{ color: heard ? "var(--ok)" : quiet ? "var(--alert)" : "var(--ink-2)" }}>
              {heard ? <><CheckCircle2 size={16} /> We can hear you. Your microphone is working.</> : quiet ? <><AlertTriangle size={16} /> We can't hear anything yet.</> : "Listening…"}
            </p>
            {quiet && !heard && (
              <ul data-testid="mic-test-tips" className="text-xs list-disc pl-5 space-y-1" style={{ color: "var(--ink-2)" }}>
                <li>Pick a different microphone below. On laptops choose the built-in "Microphone Array", not "Stereo Mix" or a virtual/meeting device.</li>
                <li>Check the laptop's mic mute key or light is off.</li>
                <li>Windows: Settings → Privacy & security → Microphone → allow desktop apps and your browser. Then Settings → System → Sound → Input: check the level moves when you talk.</li>
                <li>Close other apps using the mic (Teams, Zoom), then reopen this test.</li>
              </ul>
            )}
            <DevicePicker value={deviceId} onChange={choose} open={open} />
            <Playback stream={stream} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const MicTestButton = ({ className = "", label = "Test microphone", testid = "mic-test-open-button" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={testid} onClick={() => setOpen(true)} className={`btn-ghost rounded-full text-sm font-medium flex items-center gap-1.5 ${className}`}>
        <Mic size={14} /> {label && <span className={label === "Mic" ? "hidden sm:inline" : ""}>{label}</span>}
      </button>
      {open && <MicTestDialog open={open} onOpenChange={setOpen} />}
    </>
  );
};
